import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import type { MitMandateDTO } from '../../core/models';
import { WrapperResponse } from '../../services/service-common';
import { MitService, mitApiError } from '../../services/mit.service';

export type MitMandateStatusAction = 'suspend' | 'reactivate' | 'revoke';

const COPY: Record<MitMandateStatusAction, { title: string; text: string; confirm: string; done: string; reason: boolean }> = {
  suspend: {
    title: 'Suspender mandato',
    text: 'Mientras esté suspendido no se podrán hacer cobros. Puedes reactivarlo después.',
    confirm: 'Suspender',
    done: 'Mandato suspendido',
    reason: true
  },
  reactivate: {
    title: 'Reactivar mandato',
    text: 'El mandato vuelve a admitir cobros dentro de sus límites.',
    confirm: 'Reactivar',
    done: 'Mandato reactivado',
    reason: false
  },
  revoke: {
    title: 'Revocar mandato',
    text: 'La revocación es definitiva: ya no se podrá cobrar con este mandato ni reactivarlo.',
    confirm: 'Revocar',
    done: 'Mandato revocado',
    reason: true
  }
};

@Component({
  selector: 'app-mit-mandate-status-dialog',
  templateUrl: './mit-mandate-status-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FormsModule, DialogModule, ButtonModule, FloatLabelModule, TextareaModule]
})
export class MitMandateStatusDialogComponent {
  private mitService = inject(MitService);
  private toastr = inject(ToastrService);

  mandate = input<MitMandateDTO | null>(null);
  action = input<MitMandateStatusAction | null>(null);
  closed = output<void>();
  changed = output<MitMandateDTO>();

  reason = signal('');
  saving = signal(false);
  error = signal<string | null>(null);
  visible = computed(() => !!this.action() && !!this.mandate());
  copy = computed(() => (this.action() ? COPY[this.action()!] : null));

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.reason.set('');
        this.error.set(null);
      }
    });
  }

  close(): void {
    if (this.saving()) return;
    this.closed.emit();
  }

  confirm(): void {
    const mandate = this.mandate();
    const action = this.action();
    if (!mandate || !action || this.saving()) return;
    const reason = this.reason().trim() || undefined;
    if (action === 'revoke' && !reason) {
      this.error.set('Indica el motivo de la revocación.');
      return;
    }
    const request: Observable<WrapperResponse<MitMandateDTO>> =
      action === 'suspend'
        ? this.mitService.suspend(mandate.id, { reason })
        : action === 'reactivate'
          ? this.mitService.reactivate(mandate.id)
          : this.mitService.revoke(mandate.id, { reason });
    this.saving.set(true);
    this.error.set(null);
    request.subscribe({
      next: (response) => {
        this.saving.set(false);
        this.toastr.success(COPY[action].done);
        if (response.body) this.changed.emit(response.body);
        this.closed.emit();
      },
      error: (err) => {
        this.saving.set(false);
        this.error.set(mitApiError(err, 'No se pudo actualizar el mandato').message);
      }
    });
  }
}
